// src/pages/projects/SGBVProject.jsx
import React, { useState, useEffect } from 'react';
import { Shield, Users, Heart, BookOpen, Phone, Home, Lightbulb, TrendingUp } from 'lucide-react';

const SGBVProject = () => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const projectStats = [
    { number: '2,500+', label: 'Women & Girls Reached', icon: Users },
    { number: '95%', label: 'Survivor Support Rate', icon: Heart },
    { number: '24/7', label: 'Crisis Helpline', icon: Phone },
    { number: '18', label: 'Community Centers', icon: Home }
  ];

  const programPillars = [
    {
      icon: Shield,
      title: 'Prevention Programs',
      description: 'Community education and awareness campaigns targeting root causes of SGBV through behavioral change communication.',
      highlights: ['Community dialogue sessions', 'School-based education', 'Male engagement initiatives', 'Traditional leader involvement']
    },
    {
      icon: Heart,
      title: 'Survivor Support Services',
      description: 'Comprehensive care and support services for SGBV survivors including psychosocial support and legal assistance.',
      highlights: ['Counseling services', 'Legal aid support', 'Medical assistance', 'Economic empowerment']
    },
    {
      icon: Users,
      title: 'Community Empowerment',
      description: 'Building resilient communities through skills development, economic empowerment, and leadership training.',
      highlights: ['Vocational skills training', 'Microfinance programs', 'Leadership development', 'Peer support networks']
    },
    {
      icon: BookOpen,
      title: 'Advocacy & Policy',
      description: 'Working with stakeholders to influence policy change and strengthen systems for SGBV prevention and response.',
      highlights: ['Policy advocacy', 'Stakeholder engagement', 'Research & documentation', 'System strengthening']
    }
  ];

  const impactAreas = [
    { title: 'Zero Tolerance Culture', description: 'Building communities that reject all forms of sexual and gender-based violence' },
    { title: 'Survivor-Centered Care', description: 'Providing holistic support that prioritizes survivor needs and choices' },
    { title: 'Community Ownership', description: 'Empowering communities to lead SGBV prevention and response efforts' }
  ];

  return (
    <div className="project-page">
      {/* Bootstrap CSS */}
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet" 
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row align-items-center min-vh-80">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">Critical Initiative</div>
                <h1 className="hero-title">
                  Combating Sexual & Gender Based Violence
                </h1>
                <p className="hero-subtitle">
                  Building Safe Communities for All
                </p>
                <p className="hero-description">
                  Creating a comprehensive response to SGBV through prevention, protection, 
                  empowerment, and coordination while building a community culture of zero tolerance 
                  to all forms of gender-based violence.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-container">
                <div className="project-image">
                  <img 
                    src="/images/projects/sgbv.png" 
                    alt="SGBV Prevention Project"
                    className="hero-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="image-placeholder">
                    <Shield size={60} />
                    <span>Project Image</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="overview-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="project-image-container">
                <img 
                  src="/images/projects/sgbv-overview.jpg" 
                  alt="SGBV Project Overview"
                  className="project-overview-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder">
                  <Heart size={60} />
                  <span>Project Overview</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="overview-content">
                <div className="section-badge">Our Approach</div>
                <h2 className="section-title">Comprehensive SGBV Response</h2>
                <p className="section-description">
                  Our SGBV project adopts a holistic approach that addresses both the symptoms and 
                  root causes of gender-based violence. We work at multiple levels - from individual 
                  survivors to community systems - to create lasting change.
                </p>
                
                <div className="impact-areas">
                  <h4 className="subsection-title">Core Focus Areas</h4>
                  {impactAreas.map((area, index) => (
                    <div key={index} className="impact-item">
                      <h5 className="impact-title">{area.title}</h5>
                      <p className="impact-description">{area.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Pillars */}
      <section className="pillars-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className={`section-header text-center ${animateIn ? 'animate-in' : ''}`}>
                <div className="section-badge">Program Pillars</div>
                <h2 className="section-title">Four Pillars of Change</h2>
                <p className="section-description">
                  Our comprehensive strategy is built on four interconnected pillars that 
                  address SGBV from multiple angles for maximum impact.
                </p>
              </div>
            </div>
          </div>
          
          <div className="row">
            {programPillars.map((pillar, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="pillar-card">
                  <div className="pillar-icon">
                    <pillar.icon size={32} />
                  </div>
                  <div className="pillar-content">
                    <h3 className="pillar-title">{pillar.title}</h3>
                    <p className="pillar-description">{pillar.description}</p>
                    <ul className="pillar-highlights">
                      {pillar.highlights.map((highlight, idx) => (
                        <li key={idx}>
                          <TrendingUp size={16} />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Styling with #06b6d4 Theme */}
      <style jsx>{`
        :root {
          --primary-color: #06b6d4;
          --primary-dark: #0891b2;
          --primary-light: #67e8f9;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
          --warning-red: #ef4444;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
          padding-top: 80px;
        }

        /* Hero Section - Fixed padding */
        .hero-section {
          position: relative;
          min-height: 85vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 25px 0;
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(6,182,212,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(6,182,212,0.03)"/></svg>') repeat;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          padding-right: 2rem;
        }

        .hero-badge {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(6, 182, 212, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hero-title {
          font-size: clamp(2.5rem, 4vw, 4rem);
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 12px;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.5rem;
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 24px;
        }

        .hero-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
          max-width: 600px;
        }

        .hero-image-container {
          position: relative;
          padding-left: 2rem;
        }

        .project-image {
          position: relative;
          width: 100%;
          height: 400px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: fit-cover;
        }

        .image-placeholder {
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          color: white;
          text-align: center;
          gap: 16px;
        }

        /* Stats Section */
        .stats-section {
          padding: 80px 0;
          background: white;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 32px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        .stats-grid.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .stat-card {
          text-align: center;
          padding: 32px 24px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .stat-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 1rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        /* Overview Section */
        .overview-section {
          padding: 100px 0;
          background: var(--light-bg);
        }

        .project-image-container {
          position: relative;
        }

        .project-overview-image {
          width: 100%;
          height: 500px;
          object-fit: cover;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .overview-content {
          padding-left: 48px;
        }

        .section-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(6, 182, 212, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .section-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 24px;
          line-height: 1.2;
        }

        .section-description {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .impact-areas {
          margin-top: 48px;
        }

        .subsection-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 24px;
        }

        .impact-item {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-light);
        }

        .impact-item:last-child {
          border-bottom: none;
        }

        .impact-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--primary-color);
          margin-bottom: 8px;
        }

        .impact-description {
          color: var(--text-gray);
          margin: 0;
        }

        /* Pillars Section */
        .pillars-section {
          padding: 100px 0;
          background: white;
        }

        .section-header {
          margin-bottom: 80px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        .section-header.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .pillar-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid var(--border-light);
        }

        .pillar-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .pillar-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 24px;
        }

        .pillar-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
        }

        .pillar-description {
          color: var(--text-gray);
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .pillar-highlights {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .pillar-highlights li {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        .pillar-highlights li svg {
          color: var(--success-green);
          flex-shrink: 0;
        }

        /* Emergency Section */
        .emergency-section {
          padding: 80px 0;
          background: var(--light-bg);
        }

        .emergency-card {
          background: linear-gradient(135deg, var(--warning-red), #dc2626);
          border-radius: 20px;
          padding: 60px;
          color: white;
          text-align: center;
        }

        .emergency-content {
          display: flex;
          align-items: center;
          gap: 40px;
          max-width: 800px;
          margin: 0 auto;
        }

        .emergency-icon {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          padding: 24px;
          flex-shrink: 0;
        }

        .emergency-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .emergency-description {
          font-size: 1.1rem;
          opacity: 0.9;
          margin-bottom: 32px;
        }

        .emergency-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
        }

        .cta-card {
          text-align: center;
          padding: 80px 40px;
        }

        .cta-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: white;
          margin-bottom: 24px;
        }

        .cta-description {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 40px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .cta-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Button Styles */
        .btn {
          padding: 16px 32px;
          border-radius: 12px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          border: 2px solid transparent;
          font-size: 1rem;
        }

        .btn-primary {
          background: var(--accent-orange);
          border-color: var(--accent-orange);
          color: white;
        }

        .btn-primary:hover {
          background: #d97706;
          border-color: #d97706;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(245, 158, 11, 0.3);
        }

        .btn-outline-primary {
          background: transparent;
          border-color: var(--primary-color);
          color: var(--primary-color);
        }

        .btn-outline-primary:hover {
          background: var(--primary-color);
          color: white;
          transform: translateY(-2px);
        }

        .btn-outline-light {
          background: transparent;
          border-color: white;
          color: white;
        }

        .btn-outline-light:hover {
          background: white;
          color: var(--primary-color);
          transform: translateY(-2px);
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .overview-content {
            padding-left: 24px;
          }
          
          .hero-content {
            padding-right: 1rem;
          }
          
          .hero-image-container {
            padding-left: 1rem;
          }
        }

        @media (max-width: 992px) {
          .overview-content {
            padding-left: 0;
            margin-top: 48px;
          }
          
          .hero-content {
            padding-right: 0;
            margin-bottom: 48px;
          }
          
          .hero-image-container {
            padding-left: 0;
          }
          
          .emergency-content {
            flex-direction: column;
            text-align: center;
          }
        }

        @media (max-width: 768px) {
          .hero-section {
            padding: 20px 0;
          }
          
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 24px;
          }
          
          .pillar-card {
            padding: 32px 24px;
          }
          
          .cta-card, .emergency-card {
            padding: 60px 24px;
          }
          
          .btn {
            padding: 14px 28px;
            font-size: 0.95rem;
          }
        }

        /* Animation Utilities */
        .animate-in {
          animation: slideInUp 0.8s ease-out forwards;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default SGBVProject;